/* About and Contact, as data (2 Aug 2026). They lived as JSX inside
   `screens/company.tsx`, which was fine while the only thing that rendered
   them was React. The public Crossfire pages are rendered by Fastify and need
   the same words, and a company that describes itself differently depending on
   which renderer you reached is not one company. Same discipline as
   `shared/legal.ts`: the text is the record, the renderers are opinions about
   it.

   A paragraph may carry one link, named by the substring of its own text that
   the link wraps. Storing it that way rather than splitting the sentence into
   fragments keeps the copy readable here, which is where anyone editing it
   will be looking. */
export interface CompanyLink {
  // The exact words inside `text` that become the link.
  match: string;
  // Exactly one of these. `doc` opens another company document, `sheet` opens
  // one of the app's own modals (the landing pages have no equivalent and
  // link to the app instead), `href` is an ordinary address.
  doc?: "about" | "contact";
  sheet?: "photo-credits";
  href?: string;
}

export interface CompanyPara {
  text: string;
  link?: CompanyLink;
}

export const COMPANY_TITLES: Record<"about" | "contact", string> = {
  about: "About Practice",
  contact: "Contact",
};

export const ABOUT_PARAS: CompanyPara[] = [
  {
    text: "Practice is built by Lab 10. You follow the health voices you already trust, our agents read everything they publish, and the practical parts become a short daily practice. Every claim carries one of four evidence labels, Established, Emerging, Expert viewpoint, or Contested, and links to the exact episode, article, or post it came from.",
  },
  {
    text: "We never pick winners. When the people you follow disagree, Crossfire puts both positions in front of you, with what each rests on, and your call stays yours: dated, kept, and changeable.",
  },
  {
    text: "The only editorial ground for excluding something is accuracy: fabrication, misattribution, or misrepresenting a source. Everything else is a switch you own.",
  },
  {
    text: "Portraits on Practice are photographs of the person named, each credited to its source. See the photo credits.",
    link: { match: "See the photo credits", sheet: "photo-credits" },
  },
  {
    text: "Questions? Contact us.",
    link: { match: "Contact us", doc: "contact" },
  },
];

export const CONTACT_PARAS: CompanyPara[] = [
  {
    text: "Practice is made by Lab 10. For anything at all, corrections, questions, an expert claiming their profile, write to hello@lab10.com.",
    link: { match: "hello@lab10.com", href: "mailto:hello@lab10.com" },
  },
  {
    text: "If you spot something we got wrong about an expert or a source, please say so. Accuracy is the one standard everything here answers to, and corrections are versioned: you keep what you accepted and see what moved.",
  },
];

export const COMPANY_PARAS: Record<"about" | "contact", CompanyPara[]> = {
  about: ABOUT_PARAS,
  contact: CONTACT_PARAS,
};

/* Split a paragraph around its link. Both renderers walk the same three
   pieces, so the words before and after the link can never drift apart. */
export function splitPara(p: CompanyPara): { before: string; linked: string | null; after: string } {
  if (!p.link) return { before: p.text, linked: null, after: "" };
  const at = p.text.indexOf(p.link.match);
  if (at < 0) return { before: p.text, linked: null, after: "" };
  return {
    before: p.text.slice(0, at),
    linked: p.link.match,
    after: p.text.slice(at + p.link.match.length),
  };
}
